window.DS24 = window.DS24 || {};

DS24.gift = (() => {

    /*
    |--------------------------------------------------------------------------
    | GIFT TIERS
    |--------------------------------------------------------------------------
    |
    | You may override these values through DS24_CONFIG.freeGiftTiers:
    |
    | freeGiftTiers: [
    |     { id: 711546, threshold: 100 },
    |     { id: 711279, threshold: 200 },
    | ]
    |
    */

    const giftTiers = (
        Array.isArray(DS24_CONFIG.freeGiftTiers) &&
        DS24_CONFIG.freeGiftTiers.length
            ? DS24_CONFIG.freeGiftTiers
            : [
                {
                    id: 711546,
                    threshold: 100,
                },
                {
                    id: 711279,
                    threshold: 200,
                },
            ]
    )
        .map(tier => ({
            id: Number(tier.id),
            threshold: Number(tier.threshold),
        }))
        .filter(tier =>
            Number.isFinite(tier.id) &&
            tier.id > 0 &&
            Number.isFinite(tier.threshold) &&
            tier.threshold > 0
        )
        .sort((a, b) => a.threshold - b.threshold);

    let giftProcessing = false;
    let progressPercent = 0;
    let progressStage = null;
    let latestCart = null;
    let cartVersion = 0;

    /*
    |--------------------------------------------------------------------------
    | HELPERS
    |--------------------------------------------------------------------------
    */

    function formatMoney(amount) {
        return Number(amount || 0).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    function getGiftIds() {
        return giftTiers.map(tier => tier.id);
    }

    function getCartItems(cart) {
        return Array.isArray(cart?.items)
            ? cart.items
            : [];
    }

    /*
    |--------------------------------------------------------------------------
    | ACTIVE CART
    |--------------------------------------------------------------------------
    */

    function getActiveCartContainer() {

        const carts = [
            ...document.querySelectorAll(
                '.app_cart_tooltip_container.open'
            ),
        ];

        if (!carts.length) {
            return null;
        }

        return carts[carts.length - 1];
    }

    /*
    |--------------------------------------------------------------------------
    | REMOVE GIFT
    |--------------------------------------------------------------------------
    */

    function removeGift(giftId) {

        const cart = getActiveCartContainer();

        if (!cart) {
            return false;
        }

        const removeBtn = cart.querySelector(
            `.app_cart_delete[app-product-id="${giftId}"]`
        );

        if (!removeBtn) {
            return false;
        }

        removeBtn.click();

        return true;
    }

    /*
    |--------------------------------------------------------------------------
    | PROGRESS BAR
    |--------------------------------------------------------------------------
    */

    function getProgressBar() {

        const cart = getActiveCartContainer();

        if (!cart) {
            return null;
        }

        return cart.querySelector(
            '.free-gift-progress'
        );
    }

    function createProgressBar() {

        const existing = getProgressBar();

        if (existing) {
            return existing;
        }

        const cart = getActiveCartContainer();

        if (!cart) {
            return null;
        }

        const target = cart.querySelector(
            '.app_cart_list'
        );

        if (!target) {
            return null;
        }

        target.insertAdjacentHTML(
            'beforebegin',
            `
            <div class="free-gift-progress">

                <div class="free-gift-progress__wrapper">

                    <div class="free-gift-progress__top">

                        <div class="free-gift-progress__icon">
                            🎁
                        </div>

                        <div class="free-gift-progress__content">

                            <div class="free-gift-progress__text"></div>

                            <div class="free-gift-progress__bar">
                                <div class="free-gift-progress__fill"></div>
                            </div>

                        </div>

                    </div>

                </div>

            </div>
            `
        );

        return getProgressBar();
    }

    function getProgressState(cartSubtotal) {

        for (let index = 0; index < giftTiers.length; index++) {

            const tier = giftTiers[index];

            if (cartSubtotal < tier.threshold) {

                const previousThreshold = index > 0
                    ? giftTiers[index - 1].threshold
                    : 0;

                const segmentValue = Math.max(
                    0,
                    cartSubtotal - previousThreshold
                );

                const segmentSize = Math.max(
                    1,
                    tier.threshold - previousThreshold
                );

                return {
                    completed: false,
                    stage: index,
                    tierIndex: index,
                    remaining: tier.threshold - cartSubtotal,
                    percent: Math.min(
                        100,
                        Math.max(
                            0,
                            (segmentValue / segmentSize) * 100
                        )
                    ),
                };
            }
        }

        return {
            completed: true,
            stage: 'completed',
            tierIndex: giftTiers.length - 1,
            remaining: 0,
            percent: 100,
        };
    }

    function getProgressText(state) {

        if (state.completed) {
            return `
                🎉 Beide kostenlosen Geschenke sind freigeschaltet.
            `;
        }

        if (state.tierIndex === 0) {
            return `
                Noch
                <strong>${formatMoney(state.remaining)} EUR</strong>
                und du erhältst dein erstes kostenfreies Produkt.
            `;
        }

        return `
            🎉 1. Geschenk frei – noch <strong>${formatMoney(state.remaining)} EUR</strong> bis Geschenk 2.
        `;
    }

    function renderProgressBar(cartSubtotal) {

        const state = getProgressState(cartSubtotal);

        let progress = getProgressBar();

        if (!progress) {
            progress = createProgressBar();
        }

        if (!progress) {
            return;
        }

        const text = progress.querySelector(
            '.free-gift-progress__text'
        );

        const fill = progress.querySelector(
            '.free-gift-progress__fill'
        );

        if (!text || !fill) {
            return;
        }

        text.innerHTML = getProgressText(state);

        progress.classList.toggle(
            'unlocked',
            state.completed
        );

        /*
        |--------------------------------------------------------------------------
        | RESET THE BAR WHEN MOVING TO THE NEXT GIFT
        |--------------------------------------------------------------------------
        */

        if (progressStage !== state.stage) {
            progressStage = state.stage;
            progressPercent = 0;
            fill.style.width = '0%';
        } else {
            fill.style.width = `${progressPercent}%`;
        }

        requestAnimationFrame(() => {

            requestAnimationFrame(() => {

                fill.style.width = `${state.percent}%`;
                progressPercent = state.percent;
            });
        });
    }

    /*
    |--------------------------------------------------------------------------
    | SUBTOTAL
    |--------------------------------------------------------------------------
    */

    function parseCurrencyAmount(value) {
        if (!value) {
            return null;
        }

        /*
        |--------------------------------------------------------------------------
        | FIND THE LAST NUMBER IN THE TEXT
        |--------------------------------------------------------------------------
        |
        | Supports:
        | EUR 95,96
        | 1.234,56 EUR
        | EUR 1,234.56
        |
        */

        const matches = String(value).match(
            /-?\d[\d.,\s]*/g
        );

        if (!matches?.length) {
            return null;
        }

        let amount = matches
            .at(-1)
            .trim()
            .replace(/\s/g, '');

        const lastComma = amount.lastIndexOf(',');
        const lastDot = amount.lastIndexOf('.');

        /*
        |--------------------------------------------------------------------------
        | BOTH COMMA AND DOT
        |--------------------------------------------------------------------------
        */

        if (lastComma !== -1 && lastDot !== -1) {

            if (lastComma > lastDot) {

                // German format: 1.234,56
                amount = amount
                    .replace(/\./g, '')
                    .replace(',', '.');

            } else {

                // English format: 1,234.56
                amount = amount.replace(/,/g, '');
            }

        /*
        |--------------------------------------------------------------------------
        | COMMA ONLY
        |--------------------------------------------------------------------------
        */

        } else if (lastComma !== -1) {

            const decimalPlaces =
                amount.length - lastComma - 1;

            amount = decimalPlaces === 3
                ? amount.replace(/,/g, '')
                : amount.replace(',', '.');

        /*
        |--------------------------------------------------------------------------
        | DOT ONLY
        |--------------------------------------------------------------------------
        */

        } else if (lastDot !== -1) {

            const decimalPlaces =
                amount.length - lastDot - 1;

            if (decimalPlaces === 3) {
                amount = amount.replace(/\./g, '');
            }
        }

        const parsed = Number(amount);

        return Number.isFinite(parsed)
            ? parsed
            : null;
    }

    function subtotal(cart) {

        const cartElement = getActiveCartContainer();

        if (cartElement) {

            const amountEl = cartElement.querySelector(
                '.app_cart_amount_sum_amount'
            ) || cartElement.querySelector(
                '.app_cart_amount_sum'
            );

            const subtotalFromDom = parseCurrencyAmount(
                amountEl?.textContent
            );

            if (subtotalFromDom !== null) {
                return subtotalFromDom;
            }
        }

        /*
        |--------------------------------------------------------------------------
        | FALLBACK TO API VALUE
        |--------------------------------------------------------------------------
        */

        const netAmount = Number(
            cart?.net_amount || 0
        );

        const giftIds = getGiftIds();

        const freeGiftTotal = getCartItems(cart)
            .filter(item =>
                giftIds.includes(
                    Number(item.product_id)
                )
            )
            .reduce((total, item) => {

                const price = Number(
                    item.net_amount ??
                    item.gross_amount ??
                    0
                );

                const quantity = Number(
                    item.quantity || 0
                );

                return total + (
                    price * quantity
                );

            }, 0);

        return Math.max(
            0,
            netAmount - freeGiftTotal
        );
    }

    /*
    |--------------------------------------------------------------------------
    | GIFT STATE
    |--------------------------------------------------------------------------
    */

    function hasGift(cart, giftId) {

        return getCartItems(cart).some(item =>
            Number(item.product_id) === Number(giftId) &&
            Number(item.quantity || 0) > 0
        );
    }

    function getGiftActions(cart, cartSubtotal) {

        const removals = giftTiers
            .slice()
            .reverse()
            .filter(tier =>
                cartSubtotal < tier.threshold &&
                hasGift(cart, tier.id)
            )
            .map(tier => ({
                type: 'remove',
                giftId: tier.id,
            }));

        const additions = giftTiers
            .filter(tier =>
                cartSubtotal >= tier.threshold &&
                !hasGift(cart, tier.id)
            )
            .map(tier => ({
                type: 'add',
                giftId: tier.id,
            }));

        return [
            ...removals,
            ...additions,
        ];
    }

    /*
    |--------------------------------------------------------------------------
    | PROCESS GIFT ACTIONS SEQUENTIALLY
    |--------------------------------------------------------------------------
    */

    function processGiftActions(actions, startVersion) {

        if (!actions.length) {
            return;
        }

        giftProcessing = true;

        const runAction = index => {

            if (index >= actions.length) {

                setTimeout(() => {

                    giftProcessing = false;

                    /*
                    |--------------------------------------------------------------------------
                    | RECHECK ONLY WHEN THE CART UPDATED DURING PROCESSING
                    |--------------------------------------------------------------------------
                    */

                    if (
                        cartVersion > startVersion &&
                        latestCart
                    ) {
                        handle(latestCart);
                    }

                }, 500);

                return;
            }

            const action = actions[index];

            if (action.type === 'add') {

                ds24cart_add(
                    action.giftId,
                    1
                );

            } else {

                removeGift(
                    action.giftId
                );
            }

            setTimeout(() => {
                runAction(index + 1);
            }, 700);
        };

        runAction(0);
    }

    /*
    |--------------------------------------------------------------------------
    | HANDLE
    |--------------------------------------------------------------------------
    */

    function handle(cart) {

        latestCart = cart;
        cartVersion += 1;

        const currentVersion = cartVersion;
        const cartSubtotal = subtotal(cart);

        setTimeout(() => {
            renderProgressBar(cartSubtotal);
        }, 300);

        if (giftProcessing) {
            return;
        }

        const actions = getGiftActions(
            cart,
            cartSubtotal
        );

        processGiftActions(
            actions,
            currentVersion
        );
    }

    return {
        handle,
    };

})();
